import { Controller } from "@hotwired/stimulus"
import { openChrome, closeChrome, openOverlayScrim } from "../helpers/overlay_chrome"

// The goal-reached card (budgets/_goal_reached).
//
// Raised on the first dashboard render after the active goal tips over
// (budgets_controller#flag_goal_reached says the card is eligible). Like the
// Due today deck, the card decides the rest: it waits for the page to settle,
// stands down if another overlay is already up (the deck, a celebration), and
// the moment it is really on screen posts goals#celebrated so the goal is
// stamped and the card never plays twice.
//
// Shell copied from streak_up_controller: the raised-attribute guard against a
// restored Turbo snapshot re-raising a card the server has already consumed,
// the scroll lock, and the native chrome hand-off (tab bar away, nav bar dimmed
// to the scrim's colour) through helpers/overlay_chrome. The choreography is
// CSS: the stage scales in, then `is-landed` springs the mascot up and drops
// the confetti. Both buttons leave the page, so `leave` only puts the chrome
// back before the navigation.
export default class extends Controller {
  static targets = ["panel", "haptic"]
  static values = { celebratedUrl: String }

  static RAISED_ATTRIBUTE = "data-goal-reached-raised"
  static SHOW_DELAY = 600
  static LAND_DELAY = 420

  connect() {
    this._timers = []
    this._resetForCache = () => this._reset()
    document.addEventListener("turbo:before-cache", this._resetForCache)

    if (this.element.hasAttribute(this.constructor.RAISED_ATTRIBUTE)) return

    this._timers.push(setTimeout(() => {
      if (!openOverlayScrim()) this.show()
    }, this.constructor.SHOW_DELAY))
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._resetForCache)
    this._timers.forEach((id) => clearTimeout(id))
    this._unlockScroll()
  }

  show() {
    this.element.setAttribute(this.constructor.RAISED_ATTRIBUTE, "1")
    this._markCelebrated()
    this.panelTarget.hidden = false
    this._lockScroll()
    openChrome(this, this.element)
    // Same double-rAF re-assert the celebration popups use: the budget
    // carousel calls bridge.show() for its first slide during load, which
    // would clobber an immediate hide().
    requestAnimationFrame(() => requestAnimationFrame(() => openChrome(this, this.element)))

    this._timers.push(setTimeout(() => {
      this.element.classList.add("is-landed")
      this._fireHaptic()
    }, this.constructor.LAND_DELAY))
  }

  // Both CTAs navigate (goals#complete, goals#edit). Put the chrome back first
  // so the dim never outlives the page that raised it.
  leave() {
    this._unlockScroll()
    closeChrome(this)
  }

  // The X, copied from help_intro_controller#close. The goal was stamped on
  // show, so closing only puts the card away.
  close() {
    this.panelTarget.hidden = true
    this.element.classList.remove("is-landed")
    this._unlockScroll()
    closeChrome(this)
    document.dispatchEvent(new CustomEvent("celebration:closed"))
  }

  // turbo:before-cache: hidden, unlocked, and still marked raised.
  _reset() {
    this.panelTarget.hidden = true
    this._unlockScroll()
  }

  // The one write: the server stops offering the card once this lands. Same
  // request shape as the Due today deck's actions; a request that never lands
  // just means the card plays again next time, which beats never.
  _markCelebrated() {
    if (!this.hasCelebratedUrlValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.celebratedUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": token, Accept: "application/json" }
    }).catch(() => {})
  }

  _fireHaptic() {
    if (!this.hasHapticTarget) return
    const haptic = this.application.getControllerForElementAndIdentifier(this.hapticTarget, "bridge--haptic")
    haptic?.tap()
  }

  _lockScroll() {
    document.documentElement.style.overflow = "hidden"
  }

  _unlockScroll() {
    document.documentElement.style.overflow = ""
  }
}
